// utils/appLanguage.ts
//
// The user's preferred language (chosen at onboarding, editable from
// Account → Language) is the ONLY language source for the whole UI.
// AuthContext.applyPreferredLanguage keeps i18next in step with it, so every
// screen reads the language from i18next here instead of keeping its own
// toggle or localStorage key.
//
// Feature screens that only ship English plus one regional language use
// `pickLanguage`: the regional language when it is the user's preference,
// English otherwise — never a mix of the two.
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { isAppLanguage } from "./languages";
import { getStoredLanguage } from "./getLanguage";

const toAppLanguage = (code?: string | null): string => {
  const base = (code || "").split("-")[0];
  return isAppLanguage(base) ? base : "en";
};

/** Current UI language, for non-React code (services, request builders). */
export const getAppLanguage = (): string =>
  toAppLanguage(i18n.language || getStoredLanguage());

/** Current UI language; re-renders when the preferred language changes. */
export function useAppLanguage(): string {
  const { i18n: instance } = useTranslation();
  return toAppLanguage(instance.language);
}

/**
 * Maps the app language onto a screen's own language codes.
 * `aliases` covers screens whose code differs from the app code
 * (e.g. Philippines uses "ph" for the app's "fil").
 */
export function pickLanguage<T extends string>(
  appLanguage: string,
  supported: readonly T[],
  aliases: Partial<Record<string, T>> = {},
): T | "en" {
  const aliased = aliases[appLanguage];
  if (aliased) return aliased;
  return (supported as readonly string[]).includes(appLanguage)
    ? (appLanguage as T)
    : "en";
}

/** BCP 47 locale for Intl date/number formatting in the given app language. */
export const toLocale = (appLanguage: string): string =>
  ({ zh: "zh-CN", pt: "pt-BR", fil: "fil-PH" } as Record<string, string>)[
    appLanguage
  ] || appLanguage;

/** Formats a date in the user's preferred language (not the browser locale). */
export const formatAppDate = (
  value: string | number | Date,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  },
): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(toLocale(getAppLanguage()), options);
};
